import React, { Fragment, useState } from 'react';
import _, { get } from 'lodash';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import styles from './onboardingSidebarStyles.scss';

import AnonymousUserDisplay from 'buyside/components/presentation/AnonymousUserDisplay/anonymousUserDisplay';
import AnonEmployerAssociation from 'buyside/components/presentation/EmployerAssociation/anonEmployerAssociation';
import {
  SidebarMassImport,
  SidebarTicker,
  SidebarUser,
  SidebarWatchlistList
} from './onboardingSidebarComponents';
import LoadingSpinner from 'buyside/components/presentation/LoadingSpinner/loadingSpinner';

import { defaultUserImageUrl } from 'buyside/util/urlHelper';
import { useIsMobile } from 'buyside/util/hooks/useIsMobile';

import CaretRightIcon from 'buyside/svgs/caret_right.svg';

const OnboardingSidebar = ({
  unfollowEntity,
  removeMassImport,
  unfollowUser,
  loading,
  watchlist,
  page,
  isAllocator,
  showUserDetails
}) => {
  const [updatingTickers, setUpdatingTickers] = useState({});
  const [updatingMassImports, setUpdatingMassImports] = useState({});
  const [updatingUsers, setUpdatingUsers] = useState({});
  const [tickerExpanded, setTickerExpanded] = useState(true);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const isMobile = useIsMobile();

  const toggleExpandTickers = () => setTickerExpanded(!tickerExpanded);

  const toggleExpandSidebar = () => {
    if (sidebarExpanded) {
      window.scrollTo(0, 0);
    }
    setSidebarExpanded(!sidebarExpanded);
  };

  const handleUnfollowTicker = entityId => {
    return () => {
      if (updatingTickers[entityId]) return;

      const newState = _.cloneDeep(updatingTickers);
      newState[entityId] = true;
      setUpdatingTickers(newState);

      unfollowEntity({ variables: { entityId: entityId } }).then(() => {
        const newState = _.cloneDeep(updatingTickers);
        newState[entityId] = false;
        setUpdatingTickers(newState);
      });
    };
  };

  const handleRemoveMassImport = massImportId => {
    return () => {
      if (updatingMassImports[massImportId]) return;

      const newState = _.cloneDeep(updatingMassImports);
      newState[massImportId] = true;
      setUpdatingMassImports(newState);

      removeMassImport({ variables: { massImportId: massImportId } }).then(() => {
        const newState = _.cloneDeep(updatingMassImports);
        newState[massImportId] = false;
        setUpdatingMassImports(newState);
      });
    };
  };

  const handleUnfollowUser = userId => {
    return () => {
      if (updatingUsers[userId]) return;

      const newState = _.cloneDeep(updatingUsers);
      newState[userId] = true;
      setUpdatingUsers(newState);

      unfollowUser({ variables: { userId: userId } }).then(() => {
        const newState = _.cloneDeep(updatingUsers);
        newState[userId] = false;
        setUpdatingUsers(newState);
      });
    };
  };

  if (loading) {
    return (
      <div className={styles.onboardingSidebar}>
        <h2 className={styles.onboardingSidebarHeader}>Your Watchlist</h2>
        <LoadingSpinner type="secondary" />
      </div>
    );
  }

  const {
    sectors = [],
    geographies = [],
    assetClasses = [],
    entitiesRegularFollows = [],
    entitiesFromMassImport = [],
    massImports = [],
    users = [],
    fundTypes = [],
    fundStrategies = [],
    aumRanges = []
  } = watchlist;

  let entitiesFromMassImportPruned = _.cloneDeep(entitiesFromMassImport);
  let entitiesTruncated = false;
  if (entitiesFromMassImportPruned.length > 15) {
    entitiesFromMassImportPruned = entitiesFromMassImport.slice(0, 12);
    entitiesTruncated = true;
  }

  if (isMobile && !sidebarExpanded) {
    return (
      <div onClick={toggleExpandSidebar} id="toggle-expand-sidebar">
        <div className={styles.onboardingSidebarSmall + ` ${styles.isMobile}`}>
          <h2 className={styles.onboardingSidebarHeader}>Your Watchlist</h2>
        </div>
        <div className={styles.expandButtonArea}>
          <CaretRightIcon className={styles.arrowIcon} />
        </div>
      </div>
    );
  }

  return (
    <Fragment>
      <div className={styles.onboardingSidebar + ` ${isMobile ? styles.isMobile : ''}`}>
        <h2 className={styles.onboardingSidebarHeader}>Your Watchlist</h2>
        <div className={styles.watchlistSection}>
          <div className={styles.watchlistNumber}>1.</div>
          <div className={styles.watchlistInfo}>
            <SidebarWatchlistList title="Sectors" listItems={sectors} />
            <SidebarWatchlistList title="Geographies" listItems={geographies} />
            <SidebarWatchlistList title="Asset Classes" listItems={assetClasses} />
            {isAllocator && (
              <Fragment>
                <SidebarWatchlistList title="Fund Types" listItems={fundTypes} />
                <SidebarWatchlistList title="Fund Strategies" listItems={fundStrategies} />
                <SidebarWatchlistList title="Aum Ranges" listItems={aumRanges} />
              </Fragment>
            )}
          </div>
        </div>
        <div className={`${styles.watchlistSection} ${page < 2 ? styles.inactive : ''}`}>
          <div className={styles.watchlistNumber}>2.</div>
          <div className={styles.watchlistInfo}>
            <h3 className={styles.watchlistHeader}>
              Tickers
              {page > 1 && (
                <span className={styles.toggleButton} onClick={toggleExpandTickers}>
                  ({tickerExpanded ? 'Hide' : 'Expand'})
                </span>
              )}
            </h3>
            {tickerExpanded && (
              <div className={styles.watchlistContent}>
                <div className={styles.importedTickers}>Imported Ticker Files</div>
                <div className={styles.tickerList}>
                  <TransitionGroup>
                    {massImports.map(massImport => (
                      <CSSTransition key={massImport.id} timeout={500} classNames="fade">
                        <SidebarMassImport
                          {...massImport}
                          showRemove={page === 2}
                          removeMassImport={handleRemoveMassImport(massImport.id)}
                          disabled={updatingMassImports[massImport.id]}
                        />
                      </CSSTransition>
                    ))}
                  </TransitionGroup>
                </div>
                <div className={styles.importedTickers}>Followed Tickers</div>
                <div className={styles.tickerList}>
                  <TransitionGroup>
                    {entitiesRegularFollows.map(entity => (
                      <CSSTransition key={entity.id} timeout={500} classNames="fade">
                        <SidebarTicker
                          key={entity.id}
                          showRemove={page === 2}
                          disabled={updatingTickers[entity.id]}
                          unfollow={handleUnfollowTicker(entity.id)}
                          {...entity}
                        />
                      </CSSTransition>
                    ))}
                  </TransitionGroup>
                  <div>
                    <strong className={styles.fromImports}>From Imports</strong> (
                    {entitiesFromMassImport.length})
                  </div>
                  <TransitionGroup>
                    {entitiesFromMassImportPruned.map(entity => (
                      <CSSTransition key={entity.id} timeout={500} classNames="fade">
                        <SidebarTicker
                          key={entity.id}
                          showRemove={page === 2}
                          disabled={updatingTickers[entity.id]}
                          primarySymbol={entity.primarySymbol}
                          name={entity.name}
                          unfollow={handleUnfollowTicker(entity.id)}
                        />
                      </CSSTransition>
                    ))}
                  </TransitionGroup>
                  {entitiesTruncated && <div>and {entitiesFromMassImport.length - 12} more...</div>}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className={`${styles.watchlistSection} ${page < 3 ? styles.inactive : ''}`}>
          <div className={styles.watchlistNumber}>3.</div>
          <div className={styles.watchlistInfo}>
            <h3 className={styles.watchlistHeader}>Community</h3>
            <div>
              {users && (
                <TransitionGroup>
                  {users.map(user => (
                    <CSSTransition key={user.id} timeout={500} classNames="fade">
                      <SidebarUser
                        id={user.id}
                        disabled={updatingUsers[user.id]}
                        unfollow={handleUnfollowUser(user.id)}
                        showRemove={page === 3}
                        displayName={
                          showUserDetails ? (
                            user.displayName
                          ) : (
                            <AnonymousUserDisplay
                              anonUser={{
                                title: get(user, ['mostRecentEmployer', 'title'])
                              }}
                            />
                          )
                        }
                        currentEmployerName={
                          showUserDetails ? (
                            user.currentEmployerName
                          ) : (
                            <AnonEmployerAssociation
                              aumRange={get(
                                user,
                                ['mostRecentEmployer', 'employer', 'aumRange'],
                                ''
                              )}
                              fundType={get(
                                user,
                                ['mostRecentEmployer', 'employer', 'fundType'],
                                ''
                              )}
                            />
                          )
                        }
                        profileImageUrl={
                          showUserDetails ? user.profileImageUrl : defaultUserImageUrl()
                        }
                      />
                    </CSSTransition>
                  ))}
                </TransitionGroup>
              )}
            </div>
          </div>
        </div>
      </div>
      {isMobile && (
        <div className={styles.expandButtonArea} onClick={toggleExpandSidebar}>
          <CaretRightIcon className={styles.arrowIcon + ` ${styles.arrowUp}`} />
        </div>
      )}
    </Fragment>
  );
};

export default OnboardingSidebar;
